import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  Card,
  Button,
  Alert,
  Spin,
  Typography,
  Space,
  Tag,
  Table,
  Input,
  Drawer,
} from 'antd';
import {
  ReloadOutlined,
  InfoCircleOutlined,
  SyncOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import * as api from 'util/api';

const { Text, Paragraph } = Typography;

/** util/api axios interceptor camelizes JSON (humps): e.g. kounta_product_id → kountaProductId. */
const POLL_INTERVAL_MS = 2000;
const PAGE_SIZE = 50;

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function formatMoney(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(4) : String(v);
}

const KountaApiCache = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [startingSync, setStartingSync] = useState(false);
  const [lastSyncError, setLastSyncError] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheData, setCacheData] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rawDrawerOpen, setRawDrawerOpen] = useState(false);
  const [rawLoading, setRawLoading] = useState(false);
  const [rawPayload, setRawPayload] = useState(null);
  const [rawProductId, setRawProductId] = useState(null);
  const pollRef = useRef(null);
  const lastCompletedSyncAtRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const status = await api.getKountaApiSyncStatus();
      setSyncStatus(status);
      if (status.status === 'failed' && status.error) {
        setLastSyncError(status.error);
      }
      if (status.status === 'completed') {
        setLastSyncError(null);
      }
    } catch (err) {
      setSyncStatus({ status: 'idle', error: err.message });
    } finally {
      setLoadingStatus(false);
    }
  };

  const loadCachedProducts = useCallback(async (pageNum = 1, searchTerm = '') => {
    setCacheLoading(true);
    try {
      const offset = (pageNum - 1) * PAGE_SIZE;
      const data = await api.getKountaApiCachedProducts({
        limit: PAGE_SIZE,
        offset,
        search: searchTerm || undefined,
      });
      setCacheData(data);
    } catch (err) {
      setCacheData({ error: err.message, items: [], total: 0 });
    } finally {
      setCacheLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    if (syncStatus?.status === 'running') {
      pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    }
    if (syncStatus?.status === 'completed' || syncStatus?.status === 'failed') {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [syncStatus?.status]);

  useEffect(() => {
    if (!loadingStatus) {
      loadCachedProducts(1, '');
      setPage(1);
    }
  }, [loadingStatus, loadCachedProducts]);

  useEffect(() => {
    if (syncStatus?.status !== 'completed' || !syncStatus?.completedAt) return;
    if (lastCompletedSyncAtRef.current === syncStatus.completedAt) return;
    lastCompletedSyncAtRef.current = syncStatus.completedAt;
    setPage(1);
    loadCachedProducts(1, search);
  }, [syncStatus?.status, syncStatus?.completedAt, search, loadCachedProducts]);

  const handleStartSync = async () => {
    setStartingSync(true);
    setLastSyncError(null);
    try {
      const result = await api.syncKountaApiProducts();
      const status = result?.status;
      if (status === 202) {
        await fetchStatus();
      } else if (status === 409) {
        setLastSyncError('A sync is already in progress.');
        await fetchStatus();
      } else if (status >= 400) {
        setLastSyncError(result?.data?.error || result?.data?.message || `Request failed (${status})`);
      }
    } catch (err) {
      setLastSyncError(err.message || 'Failed to start sync');
    } finally {
      setStartingSync(false);
    }
  };

  const openRaw = async (kountaProductId) => {
    if (kountaProductId == null || kountaProductId === '') {
      setRawPayload({ error: 'Missing product id for this row' });
      setRawProductId(null);
      setRawDrawerOpen(true);
      return;
    }
    setRawProductId(kountaProductId);
    setRawDrawerOpen(true);
    setRawLoading(true);
    setRawPayload(null);
    try {
      const data = await api.getKountaApiCachedProductRaw(kountaProductId);
      setRawPayload(data?.rawPayload ?? data?.raw_payload ?? null);
    } catch (err) {
      setRawPayload({ error: err.message });
    } finally {
      setRawLoading(false);
    }
  };

  const isRunning = syncStatus?.status === 'running';
  const pagesFetched = syncStatus?.pagesFetched || 0;
  const syncedCount = syncStatus?.syncedCount || 0;

  const columns = [
    {
      title: 'Kounta ID',
      dataIndex: 'kountaProductId',
      key: 'kountaProductId',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: 'Unit price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 110,
      render: (v) => formatMoney(v),
    },
    {
      title: 'Ex tax',
      dataIndex: 'unitPriceExTax',
      key: 'unitPriceExTax',
      width: 110,
      render: (v) => formatMoney(v),
    },
    {
      title: 'Online',
      dataIndex: 'showOnline',
      key: 'showOnline',
      width: 72,
      render: (v) => {
        if (v === null || v === undefined) return '—';
        return v ? 'Yes' : 'No';
      },
    },
    {
      title: 'Synced',
      dataIndex: 'lastSyncedAt',
      key: 'lastSyncedAt',
      width: 160,
      render: (v) => (v ? formatTime(v) : '—'),
    },
    {
      title: '',
      key: 'raw',
      width: 56,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            openRaw(record.kountaProductId);
          }}
        />
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 960, marginTop: 24 }}>
      <Card
        title="Kounta API cache (parallel test)"
        extra={<Text type="secondary">REST — separate from CSV import</Text>}
      >
        <Alert
          message="Requires GCP secrets: kounta-client-id, kounta-client-secret, kounta-company-id"
          description={(
            <>
              Basic tier rate limit: 60 requests/minute (~25 products per request).
              See
              {' '}
              <a href="https://apidoc.kounta.com/" target="_blank" rel="noopener noreferrer">
                Kounta API docs
              </a>
              .
            </>
          )}
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />

        {loadingStatus ? (
          <Spin />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space wrap>
              <Button
                type="primary"
                icon={<SyncOutlined />}
                onClick={handleStartSync}
                loading={startingSync}
                disabled={isRunning}
              >
                {isRunning ? 'Syncing…' : 'Sync from Kounta API'}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => fetchStatus()} disabled={isRunning}>
                Refresh status
              </Button>
            </Space>

            {isRunning && (
              <div>
                <Tag color="processing" style={{ marginBottom: 8 }}>Running</Tag>
                <Text type="secondary">
                  Requests completed:
                  {' '}
                  {pagesFetched}
                  {' · '}
                  Products upserted:
                  {' '}
                  {syncedCount}
                </Text>
              </div>
            )}

            {syncStatus?.status === 'completed' && (
              <Alert
                message="Sync complete"
                description={(
                  <>
                    {syncedCount}
                    {' '}
                    products in
                    {' '}
                    {pagesFetched}
                    {' '}
                    requests. Completed at
                    {' '}
                    {formatTime(syncStatus.completedAt)}
                  </>
                )}
                type="success"
                showIcon
              />
            )}

            {syncStatus?.status === 'failed' && (
              <Alert
                message="Sync failed"
                description={syncStatus.error || lastSyncError || 'Unknown error'}
                type="error"
                showIcon
              />
            )}

            {syncStatus?.lastBatchSampleIds?.length > 0 && isRunning && (
              <Text type="secondary">
                Recent IDs:
                {' '}
                {syncStatus.lastBatchSampleIds.join(', ')}
              </Text>
            )}

            {syncStatus?.startedAt && (
              <Text type="secondary">
                Last run started:
                {' '}
                {formatTime(syncStatus.startedAt)}
              </Text>
            )}

            <Card size="small" title="Cached rows (API sync)" style={{ marginTop: 16 }}>
              <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search
                  placeholder="Search SKU, name, or Kounta ID"
                  allowClear
                  style={{ width: 280 }}
                  onSearch={(value) => {
                    setSearch(value);
                    setPage(1);
                    loadCachedProducts(1, value);
                  }}
                />
                <Button onClick={() => loadCachedProducts(page, search)} loading={cacheLoading}>
                  Reload table
                </Button>
              </Space>
              {cacheData?.error && (
                <Alert type="warning" message={cacheData.error} style={{ marginBottom: 8 }} />
              )}
              <Table
                size="small"
                rowKey="kountaProductId"
                loading={cacheLoading}
                columns={columns}
                dataSource={cacheData?.items || []}
                scroll={{ x: 900 }}
                pagination={{
                  current: page,
                  pageSize: PAGE_SIZE,
                  total: cacheData?.total ?? 0,
                  showSizeChanger: false,
                  onChange: (p) => {
                    setPage(p);
                    loadCachedProducts(p, search);
                  },
                }}
              />
            </Card>
          </Space>
        )}
      </Card>

      <Drawer
        title={rawProductId ? `Raw JSON — ${rawProductId}` : 'Raw JSON'}
        placement="right"
        width={720}
        onClose={() => setRawDrawerOpen(false)}
        visible={rawDrawerOpen}
        destroyOnClose
      >
        {rawLoading ? (
          <Spin />
        ) : (
          <Paragraph>
            <pre style={{ fontSize: 12, overflow: 'auto', maxHeight: '70vh' }}>
              {JSON.stringify(rawPayload, null, 2)}
            </pre>
          </Paragraph>
        )}
      </Drawer>
    </div>
  );
};

export default KountaApiCache;
